import React, { ReactNode, useState } from 'react';
import { useTranslation } from "react-i18next";
import clsx from 'clsx';
import { FormControlLabel } from '@material-ui/core';
import { Checkbox } from '../Checkbox/Checkbox';
import { Button } from '../Button/Button';
import { CollapseButton } from '../CollapseButton/CollapseButton';
import { Typography } from '../Typography/Typography';
import { Card } from '../Card/Card';
import { FieldErrorMessage } from '../form/FieldErrorMessage/FieldErrorMessage';
import { InlineButton } from '../InlineButton/InlineButton';
import { useIsMobile } from '../utils/useIsMobile';
import { ModalDialog } from '../ModalDialog/ModalDialog';

import useStyles from './ConsentItem.style';

export interface ConsentItemProps {
  id: string;
  name: string;
  label: string | React.ReactNode;
  dialogTitle?: string;
  additionalLabel?: React.ReactNode;
  description?: string | React.ReactNode;
  showDescriptionInDialog?: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: ReactNode;
}

export const ConsentItem: React.FC<ConsentItemProps> = ({
  id,
  name,
  label,
  dialogTitle,
  additionalLabel,
  description,
  showDescriptionInDialog = false,
  checked,
  onChange,
  required = false,
  className,
  disabled = false,
  hasError = false,
  errorMessage}) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  const handleShowInDialog = () => setIsDialogOpen(true);
  const handleDialogClose = () => setIsDialogOpen(false);
  const handleToggleExpansion = () => setIsExpanded(prevValue => !prevValue);

  const handleExpandClick = showDescriptionInDialog ? handleShowInDialog : handleToggleExpansion;

    const getButtonConfig = () => {
    if (showDescriptionInDialog) {
      return {
        label: t("consent-item.show-details"),
        testId: "show-details-button",
        ariaLabel: t("consent-item.aria.show-all"),
      };
    }

    if (isExpanded) {
      return {
        label: t("consent-item.collapse"),
        testId: "collapse-button",
        ariaLabel: t("consent-item.aria.collapse"),
      };
    }

    return {
      label: t("consent-item.expand"),
      testId: "expand-button",
      ariaLabel: t("consent-item.aria.expand"),
    };
  };

  const buttonConfig = getButtonConfig();

  const dialogContent = (
    <div id="consent-dialog-description">
      {description}
    </div>
  );

  const dialogFooter = (
    <div className={c.dialogFooter}>
      <FormControlLabel
        className={c.formControlDialog}
        disabled={disabled}
        control={
          <Checkbox
            name={name}
            checked={checked}
            onChange={handleCheckboxChange}
            disabled={disabled}
            required={required}
            color="primary"
          />
        }
        label={
          <Typography component="span">{label}</Typography>
        }
      />
      <Button
        onClick={handleDialogClose}
        buttonVariant="medium"
        className={c.closeButton}
      >
        {t("consent-item.close")}
      </Button>
    </div>
  );

  return (
    <div id={id} className={clsx(c.root, className)} data-questionid={name}>
      <FormControlLabel
        control={
          <Checkbox
            name={name}
            checked={checked}
            onChange={handleCheckboxChange}
            disabled={disabled}
            required={required}
            color="primary"
          />
        }
        label={
          <>
            <Typography component="span">{label}</Typography>
            {additionalLabel}
          </>
        }
        className={c.formControlLabel}
        disabled={disabled}
      />
      {hasError && errorMessage && (
        <FieldErrorMessage>{errorMessage}</FieldErrorMessage>
      )}
      {description && (
        <div className={c.expandedContentContainer}>
          {!showDescriptionInDialog && isExpanded && (
            <div className={c.expandedContent}>
              {description}
            </div>
          )}
          {showDescriptionInDialog ? (
            <InlineButton
              iconType="chevronRight"
              iconPlacement="right"
              onClick={handleExpandClick}
              ariaLabel={buttonConfig.ariaLabel}
              testId={buttonConfig.testId}
              size="small"
            >
              {buttonConfig.label}
            </InlineButton>
          ) : (
            <CollapseButton
              className={c.expandButton}
              onClick={handleExpandClick}
              aria-label={buttonConfig.ariaLabel}
              testId={buttonConfig.testId}
              expanded={isExpanded}
            >
              {buttonConfig.label}
            </CollapseButton>
          )}
        </div>
      )}
      {showDescriptionInDialog && (
        <ModalDialog
          size="lg"
          open={isDialogOpen}
          onClose={handleDialogClose}
          title={dialogTitle ?? t("consent-item.title")}
          content={dialogContent}
          customFooter={dialogFooter}
          ariaDescribedBy="consent-dialog-description"
        />
      )}
    </div>
  );
};
